import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import viewImage from "../../assets/images/icons/view.svg";
import editImage from "../../assets/images/icons/edit-2.svg";
import deleteImage from "../../assets/images/icons/delete-w.svg";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import { buildFaqPayload, buildFaqUpdatePayload } from "../../utils/cmsContent";
import { toast } from "react-toastify";

const parseFaqList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.faq)) return data.faq;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.list)) return data.list;
  return [];
};

const getFaqId = (item) => item?._id || item?.id || item?.faq_id;
const getFaqQuestion = (item) => item?.question || item?.title || "";
const getFaqAnswer = (item) => item?.answer || item?.description || item?.content || "";

const truncateText = (text, max = 120) => {
  if (!text) return "—";
  const plain = String(text).replace(/<[^>]*>/g, "");
  return plain.length > max ? `${plain.slice(0, max)}...` : plain;
};

const EMPTY_FORM = { question: "", answer: "" };

function FaqList({ role }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formModal, setFormModal] = useState(null);
  const [viewFaq, setViewFaq] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const fetchFaqs = useCallback(async () => {
    if (!role) return;

    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams({ role });
      const response = await handleApiCall("get", `${endpoints.faq}?${params}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setFaqs(parseFaqList(response?.data?.data));
      } else {
        setFaqs([]);
      }
    } catch (err) {
      console.error("Error fetching FAQ list:", err);
      setError(true);
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const closeFormModal = () => {
    setFormModal(null);
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  const openAddModal = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setFormModal("add");
  };

  const openEditModal = (item) => {
    setEditingId(getFaqId(item));
    setFormData({
      question: getFaqQuestion(item),
      answer: getFaqAnswer(item),
    });
    setFormModal("edit");
  };

  const openViewModal = (item) => {
    setViewFaq(item);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.question.trim()) {
      toast.error("Please enter a question");
      return;
    }
    if (!formData.answer.trim()) {
      toast.error("Please enter an answer");
      return;
    }

    setSaving(true);
    try {
      const question = formData.question.trim();
      const answer = formData.answer.trim();

      let response;
      if (formModal === "edit" && editingId) {
        response = await handleApiCall(
          "put",
          `${endpoints.updateFaq}/${editingId}`,
          buildFaqUpdatePayload(question, answer)
        );
      } else {
        response = await handleApiCall("post", endpoints.addFaq, buildFaqPayload(role, question, answer));
      }

      const successCodes = [UI_CONSTANTS.API_CODES.SUCCESS, UI_CONSTANTS.API_CODES.CREATED];
      if (successCodes.includes(response?.data?.code)) {
        toast.success(
          response?.data?.message ||
            (formModal === "edit" ? "FAQ updated successfully" : "FAQ added successfully")
        );
        closeFormModal();
        fetchFaqs();
      }
    } catch (err) {
      console.error("Error saving FAQ:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const faqId = getFaqId(deleteTarget);
    if (!faqId) return;

    setDeleting(true);
    try {
      const response = await handleApiCall("delete", `${endpoints.deleteFaq}/${faqId}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(response?.data?.message || "FAQ deleted successfully");
        setDeleteTarget(null);
        fetchFaqs();
      }
    } catch (err) {
      console.error("Error deleting FAQ:", err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <button
          type="button"
          className="btn btn-primary bordered weight-300"
          onClick={openAddModal}
        >
          <b>Add</b>
        </button>
      </div>

      {error && (
        <div className="text-center py-3 mb-3">
          <p className="text-danger mb-2">Failed to load FAQ list</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={fetchFaqs}>
            Retry
          </button>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-striped trim">
          <thead>
            <tr>
              <th width="80">S.no.</th>
              <th className="text-start">Question</th>
              <th className="text-start">Answer</th>
              <th width="150" className="text-center">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : faqs.length > 0 ? (
              faqs.map((item, index) => (
                <tr key={getFaqId(item) || index}>
                  <td>{index + 1}</td>
                  <td className="text-wrap text-break">{getFaqQuestion(item) || "—"}</td>
                  <td className="text-wrap text-break text-start">{truncateText(getFaqAnswer(item))}</td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <button
                        type="button"
                        className="btn btn-link p-0 mx-2"
                        onClick={() => openViewModal(item)}
                        title="View"
                      >
                        <img src={viewImage} alt="View" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-link p-0 mx-2"
                        onClick={() => openEditModal(item)}
                        title="Edit"
                      >
                        <img src={editImage} alt="Edit" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-link p-0 mx-2"
                        onClick={() => setDeleteTarget(item)}
                        title="Delete"
                      >
                        <img src={deleteImage} alt="Delete" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  No FAQ found for this role.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        show={Boolean(formModal)}
        onHide={closeFormModal}
        centered
        className="pincode-modal"
      >
        <Modal.Header closeButton className="pincode-modal__header">
          <Modal.Title>{formModal === "edit" ? "Edit FAQ" : "Add FAQ"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pincode-modal__body">
          <form onSubmit={handleFormSubmit}>
            <div className="form-group mb-3">
              <input
                type="text"
                className="form-control default"
                name="question"
                value={formData.question}
                onChange={handleFormChange}
                placeholder="Enter question"
                disabled={saving}
                autoComplete="off"
                required
              />
            </div>
            <div className="form-group mb-4">
              <textarea
                className="form-control default"
                name="answer"
                rows={5}
                value={formData.answer}
                onChange={handleFormChange}
                placeholder="Enter answer"
                disabled={saving}
                required
              />
            </div>
            <div className="d-flex justify-content-end">
              <Button
                type="button"
                variant="secondary"
                className="mr-2 rounded-lg"
                onClick={closeFormModal}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-lg" disabled={saving}>
                {saving ? "Saving..." : formModal === "edit" ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(viewFaq)}
        onHide={() => setViewFaq(null)}
        centered
        size="lg"
        dialogClassName="faq-view-modal__dialog"
        className="pincode-modal faq-view-modal"
      >
        <Modal.Header closeButton className="pincode-modal__header">
          <Modal.Title>View FAQ</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pincode-modal__body">
          <div className="form-group mb-3">
            <input
              type="text"
              className="form-control default"
              value={getFaqQuestion(viewFaq) || ""}
              readOnly
            />
          </div>
          <div className="form-group mb-4">
            <textarea
              className="form-control default faq-view-modal__answer"
              rows={10}
              value={getFaqAnswer(viewFaq) || ""}
              readOnly
            />
          </div>
          <div className="d-flex justify-content-end">
            <Button
              type="button"
              variant="secondary"
              className="rounded-lg"
              onClick={() => setViewFaq(null)}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(deleteTarget)}
        onHide={() => setDeleteTarget(null)}
        centered
        className="pincode-modal pincode-delete-modal"
      >
        <Modal.Header closeButton className="pincode-modal__header">
          <Modal.Title>Delete FAQ</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pincode-modal__body">
          <p>Are you sure you want to delete this FAQ?</p>
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="button"
              variant="secondary"
              className="mr-2 rounded-lg"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              className="rounded-lg"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default FaqList;
